/**
 * V112-3-10: the editors the scope rows share (DS Checkbox / Radio inside named fieldsets).
 *   RowEditor   — a multi-choice inheritable answer (with "More …" for the long lists) or the federal role + flow-down.
 *   SectorPicks — one sector per system (D3): a fixed-order list of buttons, the workspace's industries first and the
 *                 rest behind "More sectors". Arrow keys / Home / End move focus without picking; picking never
 *                 re-orders the list or closes the disclosure (a11y R2).
 */
import { useState, type KeyboardEvent } from 'react';
import { Checkbox, Radio } from '@aigovops/design-system';
import { Caret } from './ScopeAnswerRow';
import { FLOWDOWN, FLOWDOWN_LABEL, INDUSTRY_OPTIONS, ROW_MORE_OPTIONS, ROW_OPTIONS, label, toggleValue, type Option, type RowKey } from './scope-options';

export function RowEditor({ row, value, saved = [], onChange, stem, describedBy }: {
  row: RowKey; value: string[]; onChange: (next: string[], cleared?: string[]) => void;
  /** The saved value: a stored value outside the lists keeps its box for the whole draft (it can be ticked back). */
  saved?: string[];
  /** Id stem for the controls and the row heading (`h-<stem>`). */
  stem: string;
  describedBy?: string;
}) {
  if (row === 'federal') {
    const role = value[0] ?? '';
    return (
      <fieldset className="sc-opts" data-cols="1" aria-labelledby={`h-${stem}`} aria-describedby={describedBy}>
        {ROW_OPTIONS.federal.map((o) => (
          <Radio key={o.value} id={`${stem}-${o.value}`} name={`${stem}-role`} label={o.label} checked={role === o.value}
            onChange={() => onChange(o.value === 'government_contractor' ? [o.value, ...(value.includes(FLOWDOWN) ? [FLOWDOWN] : [])] : [o.value])} />
        ))}
        {role === 'government_contractor' ? (
          <div className="sc-indent">
            <Checkbox id={`${stem}-flowdown`} label={FLOWDOWN_LABEL} checked={value.includes(FLOWDOWN)}
              onChange={(e) => onChange(e.currentTarget.checked ? [role, FLOWDOWN] : [role])} />
          </div>
        ) : null}
      </fieldset>
    );
  }
  const primary = ROW_OPTIONS[row];
  const more = ROW_MORE_OPTIONS[row];
  // A stored value outside both lists still shows (and can be unticked).
  const known = new Set([...primary, ...(more?.options ?? [])].map((o) => o.value));
  const extra: Option[] = [...new Set([...saved, ...value])].filter((v) => !known.has(v)).map((v) => ({ value: v, label: label(row, v) }));
  const box = (o: Option) => (
    <Checkbox key={o.value} id={`${stem}-${o.value}`} label={o.label} checked={value.includes(o.value)}
      onChange={(e) => {
        const next = toggleValue(row, value, o.value, e.currentTarget.checked);
        onChange(next, value.filter((x) => !next.includes(x) && x !== o.value));
      }} />
  );
  return (
    <fieldset className="sc-opts" data-cols={row === 'decision_types' ? '2' : undefined} aria-labelledby={`h-${stem}`} aria-describedby={describedBy}>
      {[...primary, ...extra].map(box)}
      {more ? (
        <details style={{ gridColumn: '1 / -1' }} open={value.some((v) => more.options.some((o) => o.value === v)) || undefined}>
          <summary><Caret />{more.label}</summary>
          <div className="sc-opts">{more.options.map(box)}</div>
        </details>
      ) : null}
    </fieldset>
  );
}

export function SectorPicks({ value, workspaceIndustries, onPick, describedBy }: {
  value: string | null; workspaceIndustries: string[]; onPick: (code: string) => void; describedBy?: string;
}) {
  const top = INDUSTRY_OPTIONS.filter((o) => workspaceIndustries.includes(o.value));
  const rest = INDUSTRY_OPTIONS.filter((o) => !workspaceIndustries.includes(o.value));
  const [moreOpen, setMoreOpen] = useState(() => rest.some((o) => o.value === value) || top.length === 0);
  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const list = [...(e.currentTarget.closest('[data-picks]')?.querySelectorAll<HTMLButtonElement>('.sc-pick') ?? [])].filter((b) => !b.closest('details:not([open])'));
    const i = list.indexOf(e.currentTarget);
    const to = ({ ArrowDown: i + 1, ArrowRight: i + 1, ArrowUp: i - 1, ArrowLeft: i - 1, Home: 0, End: list.length - 1 } as Record<string, number>)[e.key];
    if (to === undefined || !list.length) return;
    e.preventDefault();
    list[(to + list.length) % list.length]!.focus();
  };
  const btn = (o: Option) => (
    <li key={o.value}>
      <button type="button" className="sc-pick" aria-pressed={value === o.value} onKeyDown={onKey} onClick={() => onPick(o.value)}>{o.label}</button>
    </li>
  );
  return (
    <div role="group" aria-labelledby="h-sector" aria-describedby={describedBy} data-picks>
      {top.length ? <ul className="sc-picks" role="list">{top.map(btn)}</ul> : null}
      <details open={moreOpen} onToggle={(e) => setMoreOpen((e.currentTarget as HTMLDetailsElement).open)}>
        <summary><Caret />{top.length ? 'More sectors' : 'Sectors'}</summary>
        <ul className="sc-picks" role="list">{rest.map(btn)}</ul>
      </details>
    </div>
  );
}
